/**
 * Iter 110p — RFC 5322 address-list parser.
 *
 * Splits a header value like
 *   "Voutourlis, Andreas" <andreas.voutourlis@melia.com>, "Chen, Tracy" <tracy.chen@melia.com>
 * into 2 items, NOT 4. The naïve `.split(/[,;]/)` and the older
 * `/[,;](?![^<]*>)/g` heuristic both broke when display names used the
 * "Last, First" convention (Outlook default), producing red broken chips
 * on Reply-All.
 *
 * Rules implemented:
 *   - Commas / semicolons are separators ONLY when:
 *       * not inside a "double-quoted" display name, AND
 *       * not inside an <angle-bracketed> address.
 *   - Backslash escapes (RFC 5322 quoted-pair) pass through literally so
 *     `"O\"Brien" <o@b.com>` stays as one entry.
 *   - Trailing whitespace is trimmed; empty pieces are dropped.
 *   - Tabs / newlines in pasted input are normalised to commas first
 *     (used by the paste-explode flow in AddressBookInput).
 *
 * Returns: `string[]` of trimmed address tokens preserving their
 * original "Name <addr>" formatting.
 */
export function splitAddresses(raw, { normaliseWhitespace = false } = {}) {
  if (!raw) return [];
  const src = normaliseWhitespace
    ? String(raw).replace(/[\n\r\t]+/g, ",")
    : String(raw);
  const out = [];
  let cur = "";
  let inQuote = false;
  let inAngle = false;
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (ch === "\\" && i + 1 < src.length) {
      // RFC 5322 quoted-pair — pass through both chars literally.
      cur += ch + src[i + 1];
      i += 1;
      continue;
    }
    if (ch === '"' && !inAngle) { inQuote = !inQuote; cur += ch; continue; }
    if (ch === "<" && !inQuote) { inAngle = true; cur += ch; continue; }
    if (ch === ">" && !inQuote) { inAngle = false; cur += ch; continue; }
    if ((ch === "," || ch === ";") && !inQuote && !inAngle) {
      const piece = cur.trim();
      if (piece) out.push(piece);
      cur = "";
      continue;
    }
    cur += ch;
  }
  const tail = cur.trim();
  if (tail) out.push(tail);
  return out;
}
